import { defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from "vue-router";
import TheLoader from "@/components/TheLoader.vue";
import { isPlatform } from "@ionic/vue";
import { useStore } from "vuex";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Capacitor } from "@capacitor/core";
import { TokenManager } from '@/services/TokenManager';
import { Auth } from '@/services/AuthService';
import * as Sentry from '@sentry/vue';
export default defineComponent({
    name: 'WebView',
    components: { TheLoader },
    async setup(props) {
        const { locale, t } = useI18n({ useScope: 'global' });
        const isScannerRunning = ref(false);
        const router = useRouter();
        const openIDUrl = process.env.VUE_APP_OPENID_URL.split('//')[1];
        function removePrefixFromKeys(obj, prefix) {
            const newObject = {};
            for (const key in obj) {
                if (key.startsWith(prefix)) {
                    const newKey = key.slice(prefix.length);
                    newObject[newKey] = obj[key];
                }
                else {
                    newObject[key] = obj[key];
                }
            }
            return newObject;
        }
        const listener = async (event) => {
            const allowedOrigins = process.env.VUE_APP_ALLOWED_ORIGINS?.split(',') || [];
            if (!allowedOrigins.includes(event.origin))
                return;
            console.log({
                eventOrigin: event.origin,
                eventDataAction: event.data?.action
            });
            if (event.data) {
                const originURL = new URL(event.origin);
                switch (event.data.action) {
                    case 'getToken':
                        {
                            const { requestId, resource } = event.data;
                            try {
                                Sentry.addBreadcrumb({
                                    category: 'tokenbridge',
                                    level: 'debug',
                                    message: 'Token request received',
                                    data: { requestId, resource, origin: event.origin }
                                });
                                if (event.data.invalidate) {
                                    TokenManager.invalidate(resource);
                                }
                                const token = await TokenManager.getToken(resource);
                                const response = {
                                    action: 'setToken',
                                    requestId,
                                    resource,
                                    accessToken: token.accessToken,
                                    expiresAt: token.expiresAt
                                };
                                event.source.postMessage(response, event.origin);
                                Sentry.addBreadcrumb({
                                    category: 'tokenbridge',
                                    level: 'debug',
                                    message: 'Token sent',
                                    data: { requestId, resource }
                                });
                            }
                            catch (err) {
                                Sentry.captureException(err, {
                                    extra: {
                                        requestId,
                                        resource,
                                        origin: event.origin,
                                        reason: 'TokenManager_failed'
                                    }
                                });
                                console.log('WebView: Clear user data');
                                Auth.clearUserData();
                                await router.push({ name: 'Landing' });
                            }
                        }
                        break;
                    case 'getDevicePlatform':
                        {
                            console.log('WebView: send setDevicePlatform message', { value: isPlatform('desktop') ? 'desktop' : 'others' });
                            event.source.postMessage({ action: 'setDevicePlatform', value: isPlatform('desktop') ? 'desktop' : 'others' }, event.origin);
                        }
                        break;
                    case 'getTheoryAttendanceData':
                        store.commit('showQRScanner', { title: t('theoryAttendance'), type: 'theory-attendance' });
                        break;
                    case 'setInStorage':
                        {
                            const eventDataItems = typeof event.data.items === 'object' ? event.data.items : {};
                            Object.keys(eventDataItems).forEach(key => {
                                localStorage.setItem(originURL.hostname + '-' + key, eventDataItems[key]);
                            });
                        }
                        break;
                    case 'getFromStorage':
                        {
                            const storageItems = {};
                            Object.keys(localStorage)
                                .filter(key => key.startsWith(originURL.hostname) && localStorage.getItem(key))
                                .forEach(key => {
                                if (!(key in storageItems))
                                    storageItems[key] = '';
                                storageItems[key] = localStorage.getItem(key);
                            });
                            const withRemovedKeysOrigins = removePrefixFromKeys(storageItems, originURL.hostname + '-');
                            event.source.postMessage({ action: 'setInStorage', items: withRemovedKeysOrigins }, event.origin);
                        }
                        break;
                }
            }
        };
        const store = useStore();
        const theme = ref('light');
        const mediaQueryForPrefersColorScheme = window.matchMedia('(prefers-color-scheme: dark)');
        const setThemeColor = async (value) => {
            theme.value = value ? 'dark' : 'light';
            if (Capacitor.getPlatform() !== 'web') {
                await StatusBar.setStyle({ style: value ? Style.Dark : Style.Light });
            }
        };
        const setThemeColorFromEvent = (event) => setThemeColor(event.matches);
        watch(() => store.state.themePreference, async (value) => {
            if (value === 'system') {
                mediaQueryForPrefersColorScheme.addEventListener("change", setThemeColorFromEvent);
            }
            else {
                mediaQueryForPrefersColorScheme.removeEventListener("change", setThemeColorFromEvent);
            }
        });
        onMounted(() => {
            window.addEventListener("message", listener);
            if (store.state.themePreference === 'system') {
                mediaQueryForPrefersColorScheme.addEventListener("change", setThemeColorFromEvent);
            }
            const isSystemThemePreferenceDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
            const isThemeDark = store.state.themePreference === 'system' ? isSystemThemePreferenceDark : store.state.themePreference === 'dark';
            setThemeColor(isThemeDark);
        });
        onUnmounted(() => window.removeEventListener("message", listener));
        let queryStringParams = '';
        if (props.queryString) {
            queryStringParams = Object.keys(props.queryString)
                .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(props.queryString?.[key] || ''))
                .join('&');
        }
        const iframeLoading = ref(true);
        const iframeLoaded = () => {
            iframeLoading.value = false;
        };
        return { locale: locale.value, theme, queryStringParams, isScannerRunning, openIDUrl, iframeLoading, iframeLoaded };
    },
    props: {
        url: {
            type: String,
            required: true
        },
        sendOpenIDUrl: {
            type: Boolean,
            default: true
        },
        queryString: {
            type: Object,
            required: false
        }
    }
});
